import { SPHERE_RADIUS } from "../LevelMenuIslands";
import React, { useRef, useMemo } from "react";
import { useFrame } from "@react-three/fiber";
import * as THREE from "three";
import CustomShaderMaterial from "three-custom-shader-material";
import { PLANET_OUTLINE_THICKNESS, PLANET_OUTLINE_OPACITY, OUTLINE_COLOR } from "../../config/game";

import vertexShader from '../../shaders/vertex.glsl?raw';
import fragmentShader from '../../shaders/fragment.glsl?raw';

const COLOR_BASE_NEAR = new THREE.Color(0x2C7ECF);
const COLOR_BASE_FAR = new THREE.Color(0x031566);
const WAVE_SPEED = 1.2; // min: 0.5, max: 2
const WAVE_AMPLITUDE = 0.0; // min: 0.05, max: 0.5
const TEXTURE_SIZE = 20; // min: 1 , max: 80

// Stylized Ocean Sphere Component with custom shader
export default function OceanSphere() {
  const materialRef = useRef<THREE.ShaderMaterial>(null);
  
  // Memoize uniforms to prevent material recreation on every render
  const uniforms = useMemo(() => ({
    uTime: { value: 0 },
    uColorFar: { value: COLOR_BASE_FAR },
    uWaveSpeed: { value: WAVE_SPEED },
    uWaveAmplitude: { value: WAVE_AMPLITUDE },
    uTextureSize: { value: TEXTURE_SIZE },
    uWaveColor: { value: new THREE.Color(1, 1, 1) },
    uWaveOpacity: { value: 0.7 }
  }), []);
  
  // Update shader time
  useFrame(({ clock }) => {
    if (!materialRef.current) return
    if (materialRef.current.uniforms) {
      materialRef.current.uniforms.uTime.value = clock.getElapsedTime()
    }
  })

  const outlineMaterial = useMemo(() => new THREE.ShaderMaterial({
    side: THREE.BackSide,
    transparent: true,
    uniforms: {
      outlineThickness: { value: PLANET_OUTLINE_THICKNESS },
      outlineColor: { value: OUTLINE_COLOR },
      outlineOpacity: { value: PLANET_OUTLINE_OPACITY },
    },
    vertexShader: `
      #include <common>
      #include <logdepthbuf_pars_vertex>
      uniform float outlineThickness;
      void main() {
        vec4 viewPos = modelViewMatrix * vec4(position, 1.0);
        vec3 viewNormal = normalize(normalMatrix * normal);
        float dist = -viewPos.z;
        viewPos.xyz += viewNormal * outlineThickness * dist * 0.001;
        gl_Position = projectionMatrix * viewPos;
        #include <logdepthbuf_vertex>
      }
    `,
    fragmentShader: `
      #include <logdepthbuf_pars_fragment>
      uniform vec3 outlineColor;
      uniform float outlineOpacity;
      void main() {
        #include <logdepthbuf_fragment>
        gl_FragColor = vec4(outlineColor, outlineOpacity);
      }
    `,
  }), []);

  return (
    <>
      <mesh position={[0, 0, 0]}>
        <sphereGeometry
          args={[
            SPHERE_RADIUS + 0.5, // Slightly larger than planet
            64, // High detail for smooth waves
            64
          ]}
        />
        <CustomShaderMaterial
          ref={materialRef as any}
          baseMaterial={THREE.MeshStandardMaterial}
          vertexShader={vertexShader}
          fragmentShader={fragmentShader}
          uniforms={uniforms}
          color={COLOR_BASE_NEAR}
          transparent
          side={THREE.DoubleSide}
        />
      </mesh>
      <mesh position={[0, 0, 0]} renderOrder={1} frustumCulled={false} material={outlineMaterial}>
        <sphereGeometry args={[SPHERE_RADIUS + 0.5, 64, 64]} />
      </mesh>
    </>
  );
}